import { useEffect, useState } from "react";
import api, { fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Loader2, X, FileDown, FileText, Save, Send, CheckCircle2, Eye, AlertTriangle, XCircle, Ban, Repeat, Settings } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  borrador: { label: "Borrador", color: "#94a3b8" },
  revision: { label: "En revisión", color: "#22d3ee" },
  observado: { label: "Observado", color: "#fbbf24" },
  rechazado: { label: "Rechazado", color: "#f87171" },
  autorizado: { label: "Autorizado", color: "#4ade80" },
  emitido: { label: "Emitido", color: "#60a5fa" },
  cancelado: { label: "Cancelado", color: "#64748b" },
  sustituido: { label: "Sustituido", color: "#a78bfa" },
};
const Badge = ({ status }) => {
  const m = STATUS_META[status] || { label: status, color: "#94a3b8" };
  return <span className="text-[10px] px-2 py-0.5 rounded-sm font-mono uppercase" style={{ color: m.color, background: `${m.color}1a` }}>{m.label}</span>;
};

function FolioConfig() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/oficios/folio-config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  if (!cfg) return null;
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/oficios/folio-config", { prefix: cfg.prefix, padding: cfg.padding });
      toast.success("Configuración de folios guardada");
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  const year = new Date().getFullYear();
  return (
    <div className="panel p-3 flex items-center gap-3 flex-wrap" data-testid="folio-config">
      <Settings className="w-4 h-4 text-slate-400" />
      <div className="label-mono">FOLIOS:</div>
      <input data-testid="folio-prefix" value={cfg.prefix} onChange={(e) => setCfg({ ...cfg, prefix: e.target.value })}
        className="input-cyber text-xs w-48" placeholder="INCONTI/IGIFE" />
      <span className="font-mono text-xs text-cyan-300" data-testid="folio-sample">
        → {cfg.prefix || "PREFIJO"}/{year}/{"1".padStart(cfg.padding || 3, "0")}
      </span>
      <button data-testid="folio-save-btn" onClick={save} disabled={saving} className="btn-ghost px-2 py-1 text-xs uppercase">
        {saving ? "..." : "Guardar"}
      </button>
    </div>
  );
}

function CreateModal({ templates, contracts, onClose, onCreated }) {
  const [form, setForm] = useState({ template_key: "", contract_id: "", fields: {} });
  const [saving, setSaving] = useState(false);
  const setField = (k, v) => setForm((f) => ({ ...f, fields: { ...f.fields, [k]: v } }));
  const create = async () => {
    if (!form.template_key) return toast.error("Selecciona una plantilla");
    setSaving(true);
    try {
      const { data } = await api.post("/oficios", { ...form, contract_id: form.contract_id || null });
      toast.success("Borrador creado");
      onCreated(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al crear"); }
    finally { setSaving(false); }
  };
  const FIELDS = [["destinatario", "Destinatario"], ["destinatario_cargo", "Cargo del destinatario"],
    ["dependencia", "Dependencia"], ["periodo", "Periodo"], ["importe", "Importe"],
    ["referencias", "Referencias"], ["anexos", "Anexos"]];
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="panel w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="oficio-create-modal">
        <div className="flex items-center justify-between">
          <div className="font-display font-bold text-slate-100">Nuevo oficio</div>
          <button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div>
          <label className="label-mono block mb-1">Plantilla *</label>
          <select data-testid="oficio-template-select" value={form.template_key} onChange={(e) => setForm({ ...form, template_key: e.target.value })} className="w-full input-cyber">
            <option value="">— Seleccionar —</option>
            {templates.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-mono block mb-1">Contrato (variables automáticas)</label>
          <select data-testid="oficio-contract-select" value={form.contract_id} onChange={(e) => setForm({ ...form, contract_id: e.target.value })} className="w-full input-cyber">
            <option value="">— Sin contrato —</option>
            {contracts.map((c) => <option key={c.id} value={c.id}>{c.contract_number}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {FIELDS.map(([k, l]) => (
            <div key={k} className={k === "referencias" || k === "anexos" ? "col-span-2" : ""}>
              <label className="label-mono block mb-1">{l}</label>
              <input data-testid={`oficio-field-${k}`} value={form.fields[k] || ""} onChange={(e) => setField(k, e.target.value)} className="w-full input-cyber" />
            </div>
          ))}
        </div>
        <button data-testid="oficio-create-btn" onClick={create} disabled={saving}
          className="btn-primary w-full py-2 text-xs uppercase tracking-widest flex items-center justify-center gap-2">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Crear borrador
        </button>
      </div>
    </div>
  );
}

function Editor({ oficio, canAuthorize, onClose, onChanged }) {
  const [doc, setDoc] = useState(oficio);
  const [saving, setSaving] = useState(false);
  const editable = ["borrador", "observado"].includes(doc.status);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/oficios/${doc.id}`, {
        subject: doc.subject, body: doc.body, destinatario: doc.destinatario,
        destinatario_cargo: doc.destinatario_cargo, dependencia: doc.dependencia,
      });
      setDoc(data);
      onChanged(data);
      toast.success("Oficio guardado");
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const act = async (action, needNotes = false) => {
    let notes = "";
    if (needNotes) {
      notes = prompt("Motivo:") || "";
      if (!notes.trim()) return toast.error("Indica el motivo");
    }
    if (action === "emitir" && !confirm("¿Emitir el oficio? Se asignará folio definitivo y ya no podrá editarse.")) return;
    try {
      const { data } = await api.post(`/oficios/${doc.id}/status`, { action, notes });
      setDoc(data);
      onChanged(data);
      toast.success(action === "emitir" ? `Emitido con folio ${data.folio}` : "Estado actualizado");
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const substitute = async () => {
    if (!confirm("¿Generar documento sustituto? El oficio emitido quedará marcado como SUSTITUIDO.")) return;
    try {
      const { data } = await api.post(`/oficios/${doc.id}/substitute`);
      toast.success("Borrador sustituto creado");
      onChanged(null);
      setDoc(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const download = async (kind) => {
    try {
      const res = await api.get(`/oficios/${doc.id}/${kind}`, { responseType: "blob" });
      const type = kind === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      const url = URL.createObjectURL(new Blob([res.data], { type }));
      if (kind === "pdf") window.open(url, "_blank");
      else { const a = document.createElement("a"); a.href = url; a.download = `oficio_${(doc.folio || "borrador").replace(/\//g, "_")}.docx`; a.click(); }
    } catch { toast.error("No se pudo generar el documento"); }
  };

  const set = (k, v) => setDoc((d) => ({ ...d, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="panel w-full max-w-3xl p-5 space-y-3 max-h-[92vh] overflow-y-auto" data-testid="oficio-editor">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="font-display font-bold text-slate-100 text-sm">{doc.template_name}</div>
            <Badge status={doc.status} />
            {doc.folio && <span className="font-mono text-xs text-cyan-300" data-testid="oficio-folio">{doc.folio}</span>}
          </div>
          <button data-testid="oficio-editor-close" onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        {doc.substitutes_id && <div className="text-[10px] font-mono text-violet-300">SUSTITUYE A UN OFICIO EMITIDO ANTERIOR</div>}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-mono block mb-1">Destinatario</label>
            <input data-testid="oficio-edit-destinatario" value={doc.destinatario || ""} onChange={(e) => set("destinatario", e.target.value)} disabled={!editable} className="w-full input-cyber" />
          </div>
          <div>
            <label className="label-mono block mb-1">Cargo</label>
            <input value={doc.destinatario_cargo || ""} onChange={(e) => set("destinatario_cargo", e.target.value)} disabled={!editable} className="w-full input-cyber" />
          </div>
        </div>
        <div>
          <label className="label-mono block mb-1">Dependencia</label>
          <input value={doc.dependencia || ""} onChange={(e) => set("dependencia", e.target.value)} disabled={!editable} className="w-full input-cyber" />
        </div>
        <div>
          <label className="label-mono block mb-1">Asunto</label>
          <input data-testid="oficio-edit-subject" value={doc.subject || ""} onChange={(e) => set("subject", e.target.value)} disabled={!editable} className="w-full input-cyber" />
        </div>
        <div>
          <label className="label-mono block mb-1">Cuerpo del oficio</label>
          <textarea data-testid="oficio-edit-body" rows={12} value={doc.body || ""} onChange={(e) => set("body", e.target.value)} disabled={!editable}
            className="w-full input-cyber text-sm leading-relaxed" />
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {editable && (
            <button data-testid="oficio-save-btn" onClick={save} disabled={saving} className="btn-primary px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-1.5">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Guardar
            </button>
          )}
          {["borrador", "observado"].includes(doc.status) && (
            <button data-testid="oficio-send-review-btn" onClick={() => act("enviar_revision")} className="btn-ghost px-3 py-2 text-xs uppercase flex items-center gap-1.5" style={{ borderColor: "var(--cyan)", color: "var(--cyan)" }}>
              <Send className="w-3.5 h-3.5" /> Enviar a revisión
            </button>
          )}
          {doc.status === "revision" && canAuthorize && (
            <>
              <button data-testid="oficio-authorize-btn" onClick={() => act("autorizar")} className="btn-ghost px-3 py-2 text-xs uppercase flex items-center gap-1.5" style={{ borderColor: "#4ade80", color: "#86efac" }}>
                <CheckCircle2 className="w-3.5 h-3.5" /> Autorizar
              </button>
              <button data-testid="oficio-observe-btn" onClick={() => act("observar", true)} className="btn-ghost px-3 py-2 text-xs uppercase flex items-center gap-1.5" style={{ borderColor: "#fbbf24", color: "#fcd34d" }}>
                <AlertTriangle className="w-3.5 h-3.5" /> Observar
              </button>
              <button data-testid="oficio-reject-btn" onClick={() => act("rechazar", true)} className="btn-ghost px-3 py-2 text-xs uppercase flex items-center gap-1.5" style={{ borderColor: "#f87171", color: "#fca5a5" }}>
                <XCircle className="w-3.5 h-3.5" /> Rechazar
              </button>
            </>
          )}
          {doc.status === "autorizado" && (
            <button data-testid="oficio-emit-btn" onClick={() => act("emitir")} className="btn-primary px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" /> Emitir (asignar folio)
            </button>
          )}
          {doc.status === "emitido" && canAuthorize && (
            <button data-testid="oficio-substitute-btn" onClick={substitute} className="btn-ghost px-3 py-2 text-xs uppercase flex items-center gap-1.5" style={{ borderColor: "#a78bfa", color: "#c4b5fd" }}>
              <Repeat className="w-3.5 h-3.5" /> Sustituir
            </button>
          )}
          {["borrador", "revision", "observado", "autorizado"].includes(doc.status) && canAuthorize && (
            <button data-testid="oficio-cancel-btn" onClick={() => act("cancelar")} className="btn-ghost px-3 py-2 text-xs uppercase flex items-center gap-1.5 text-slate-400">
              <Ban className="w-3.5 h-3.5" /> Cancelar
            </button>
          )}
          <div className="flex-1" />
          <button data-testid="oficio-pdf-btn" onClick={() => download("pdf")} className="btn-ghost px-3 py-2 text-xs uppercase flex items-center gap-1.5" style={{ borderColor: "var(--cyan)", color: "var(--cyan)" }}>
            <FileDown className="w-3.5 h-3.5" /> PDF
          </button>
          <button data-testid="oficio-word-btn" onClick={() => download("word")} className="btn-ghost px-3 py-2 text-xs uppercase flex items-center gap-1.5" style={{ borderColor: "#60a5fa", color: "#93c5fd" }}>
            <FileText className="w-3.5 h-3.5" /> Word
          </button>
        </div>
        {(doc.history || []).length > 0 && (
          <div className="pt-2 border-t" style={{ borderColor: "var(--border)" }}>
            <div className="label-mono mb-1">// HISTORIAL</div>
            <div className="space-y-0.5 max-h-28 overflow-y-auto">
              {[...doc.history].reverse().map((h, i) => (
                <div key={i} className="text-[10px] font-mono text-slate-500">
                  {fmtDate(h.at)} · {h.by} · <span className="text-slate-300 uppercase">{h.action}</span>{h.notes && ` — ${h.notes}`}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Oficios() {
  const { user } = useAuth();
  const canAuthorize = ["admin", "contador"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/oficios", { params: statusFilter ? { status: statusFilter } : {} });
      setItems(data);
    } catch { toast.error("No se pudieron cargar los oficios"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => {
    api.get("/oficios/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/contracts").then((r) => setContracts(r.data)).catch(() => {});
  }, []);

  const openOficio = async (id) => {
    try {
      const { data } = await api.get(`/oficios/${id}`);
      setEditing(data);
    } catch { toast.error("No se pudo abrir el oficio"); }
  };

  if (loading) return <div className="p-8 text-center text-slate-400">Cargando…</div>;

  return (
    <div className="space-y-4" data-testid="legal-oficios-tab">
      <FolioConfig />
      <div className="flex items-center gap-3 flex-wrap">
        <button data-testid="oficio-new-btn" onClick={() => setCreating(true)}
          className="btn-primary px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2">
          <Plus className="w-4 h-4" /> Nuevo oficio
        </button>
        <select data-testid="oficio-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-cyber text-xs">
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
      </div>
      {items.length === 0 && <div className="text-sm text-slate-500 italic">Sin oficios. Crea el primero desde una plantilla.</div>}
      <div className="panel overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b" style={{ borderColor: "var(--border)" }}>
              {["Folio", "Asunto", "Contrato", "Destinatario", "Estado", "Fecha", ""].map((h) => <th key={h} className="label-mono px-3 py-2">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.id} className="border-b hover:bg-slate-800/30 cursor-pointer" style={{ borderColor: "var(--border)" }}
                onClick={() => openOficio(o.id)} data-testid={`oficio-row-${o.id}`}>
                <td className="px-3 py-2 font-mono text-xs text-cyan-300">{o.folio || "—"}</td>
                <td className="px-3 py-2 text-slate-200 max-w-[280px] truncate">{o.subject}</td>
                <td className="px-3 py-2 font-mono text-xs text-slate-400">{o.contract_number || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-400 max-w-[160px] truncate">{o.destinatario || "—"}</td>
                <td className="px-3 py-2"><Badge status={o.status} /></td>
                <td className="px-3 py-2 font-mono text-xs text-slate-400">{fmtDate(o.created_at)}</td>
                <td className="px-3 py-2"><Eye className="w-4 h-4 text-slate-500" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {creating && (
        <CreateModal templates={templates} contracts={contracts} onClose={() => setCreating(false)}
          onCreated={(d) => { setCreating(false); setEditing(d); load(); }} />
      )}
      {editing && (
        <Editor oficio={editing} canAuthorize={canAuthorize} onClose={() => { setEditing(null); load(); }}
          onChanged={() => load()} />
      )}
    </div>
  );
}
